import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import PlainTableSuggestedPlotsButton from 'notebook/ai-tools/suggest-plots/PlainTableSuggestedPlotsButton';
import CaptionEditable, {
  CaptionEditableContentProps,
  PreviewPluginContent,
  TablePluginContent,
} from 'notebook/mediocre/base-plugin/CaptionEditable';
import {createCollapseId} from 'notebook/mediocre/base-plugin/createCollapseId';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {TableValidationCellError} from 'notebook/structured-tables/StructuredTableApiConstants';
import {generatePlainTableProvenanceUuid} from 'notebook/tables/augmentNotePartTablesWithValues';
import TableEditable, {hiddenInputCid} from 'notebook/tables/core-ui/TableEditable';
import excess from 'notebook/tables/excess';
import {EvaluatedTable, Selector} from 'notebook/tables/excess/Evaluator';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import makeTableCSV from 'notebook/tables/export/makeTableCSV';
import {Table, TableIndex, TABLE_NAME_WELL_PREFIX} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import IntermediateBinder from 'ui-library/IntermediateBinder';
import {initiateTextDownload} from 'util/TextToBlobUtil';
import {ElementClipboardEvent, Optional} from 'util/Types';

import {
  Editor,
  EditorProps,
  MediocrePluginComponent,
  MediocreSelection,
  PasteEvent,
  SelectionPartition,
  TabEvent,
} from '../EditorTypes';
import {Format} from '../formatted-text/FormattersConstants';
import {MediocreSearchResultProps} from '../search/SearchResultProps';
import {MetadataForRestore} from '../selection/block/MultiSelectListUtil';

interface MediocrePluginComponentSelection<TEditor extends Editor<TRoot, TItem>, TRoot, TItem> {
  selectionInit: (this: TEditor, item: TItem) => MediocreSelection | null;
  selectionAtStart: (this: TEditor, item: TItem) => MediocreSelection | null;
  selectionAtEnd: (this: TEditor, item: TItem) => MediocreSelection | null;
}

interface PlainTable extends Table {
  rowLabels?: Array<string>; // Explicitly no JSX.Element
}

function selectionAtStart<TEditor extends Editor<TRoot, TItem>, TRoot, TItem>(
  this: TEditor,
  item: TItem
): MediocreSelection {
  const cid = this.props.getCid(item);
  const start = 0;
  const end = 0;
  return {selection: {cid, start, end}};
}

function selectionAtEnd<TEditor extends Editor<TRoot, TItem>, TRoot, TItem>(
  this: TEditor,
  item: TItem
): MediocreSelection {
  const cid = this.props.getCid(item);
  const {text, formats} = this.getText(item);
  const {start, end} = formatters.getSelectionIndicesFromFormatIndices(formats, text.length, text.length);
  return {selection: {cid, start, end}};
}

export const TEXT_PLUGIN_COMPONENT_BASE = {
  selectionInit: selectionAtEnd,
  selectionAtStart,
  selectionAtEnd,
  default(): {text: string; formats: Format} {
    return {text: '', formats: formatters.defaultFormats()};
  },
};

const blockPluginBase: MediocrePluginComponentSelection<Editor, unknown, unknown> = {
  selectionInit(): MediocreSelection | null {
    return null;
  },
  selectionAtStart(): MediocreSelection | null {
    return null;
  },
  selectionAtEnd(): MediocreSelection | null {
    return null;
  },
};

const selectablePluginBase: MediocrePluginComponentSelection<Editor, unknown, unknown> = {
  selectionInit(this: Editor): MediocreSelection | null {
    return null;
  },
  selectionAtStart(this: Editor, item: unknown): MediocreSelection | null {
    return this.selectionAtItem(item);
  },
  selectionAtEnd(this: Editor, item: unknown): MediocreSelection | null {
    return this.selectionAtItem(item);
  },
};

function tableFullSelection<TEditor extends Editor<TRoot, TItem>, TRoot, TItem>(
  this: TEditor,
  item: TItem
): MediocreSelection {
  return {
    selection: {
      cid: hiddenInputCid(this.props.getCid(item)),
      start: 0,
      end: 0,
    },
  };
}
interface TableItemProps {
  forceLiveness: boolean;
  tableNamespace: string;
  showExcessLabels: boolean;
  computedTable: EvaluatedTable;
  validationErrors: Array<TableValidationCellError>;
  table: Table;
  cellColors: {[rowIndex: number]: {[columnIndex: number]: string}} | undefined;
  multiEditing: boolean;
  selectors: Array<Selector>;
  selectorCounts: {[selectorId: number]: number};
  // To avoid unnecessary updates, only pass metadataForRestore to component if it is relevant to the
  // component.
  metadataForRestore: MetadataForRestore<TableIndex> | null;
  isMaximized?: boolean;
  isFocused?: boolean;
  isSplitScreen?: boolean;
  banner?: string | JSX.Element;
}

/**
 * The information necessary for a TableEditable to render, which is mainly being threaded via
 * SpreadsheetEvalMixin. The editor also has selection restoration information which we only expose the
 * TableEdidtable if th selection corresponds to it to avoid preventing the MediocreItem from avoiding
 * re-rendering.
 */
function getTableItemProps<TEditor extends Editor<TRoot, TItem>, TRoot, TItem>(
  this: TEditor,
  item: TItem,
  index: number
): TableItemProps {
  const tableName = this.getSpreadsheetTableName(item);
  const cid = this.getCid(item);
  const {
    tableNamespace,
    showExcessLabels,
    computedTables,
    cellColors,
    editingTable,
    selectorCounts,
    fullTableMap,
  } = this.getDataForTableItemProps();
  const validationErrorSnapshot = this.getValidationErrorSnapshot() ?? {};
  return {
    forceLiveness: this.state.forceLiveness,
    tableNamespace,
    showExcessLabels,
    computedTable: computedTables[tableName],
    validationErrors: validationErrorSnapshot[tableName],
    table: fullTableMap[tableName],
    cellColors: cellColors != null ? cellColors[tableName] : undefined,
    multiEditing: editingTable === tableName,
    selectors: this.getRoot().selectors,
    isFocused: this.isTableFocused(item),
    isMaximized: this.isTableFocused(item) && this.shouldRenderMaximizedTable(),
    isSplitScreen: this.isTableFocused(item) && this.shouldRenderSplitScreenTable(),
    selectorCounts,
    // To avoid unnecessary updates, only pass metadataForRestore to component if it is relevant to the
    // component.
    metadataForRestore:
      this.props.metadataForRestore != null &&
      (this.props.metadataForRestore as MetadataForRestore<unknown>).multiSelectKey ===
        TableUtil.getTableMultiSelectKey(cid)
        ? (this.props.metadataForRestore as MetadataForRestore<TableIndex>)
        : null,
  };
}

async function handleDownloadAsCSV({
  table,
  computedTable,
}: {
  table: PlainTable;
  computedTable: EvaluatedTable;
}): Promise<void> {
  const {table: cells, name, columnLabels, rowLabels} = table;
  initiateTextDownload(
    await makeTableCSV({
      table: cells,
      computedTable,
      headerRow: TableUtil.columnLabelsToText(columnLabels),
      rowLabels,
    }),
    `${name}.csv`
  );
  analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.DOWNLOADED_UNSTRUCTURED_OR_WELL_PLATE_TABLE_AS_CSV);
}

function makeTable<TEditor extends Editor<TRoot, TItem>, TRoot, TItem>(
  this: TEditor,
  item: TItem & {table: PlainTable},
  index: number,
  editorProps: EditorProps<TItem> & {disableFormatting?: boolean; disableMaximizedTables?: boolean},
  itemProps: TableItemProps,
  searchResultProps: MediocreSearchResultProps,
  param: Optional<{tableItemCid?: string}>
): JSX.Element {
  // Note: Do not directly access @props or @state or indirectly through a method call here (D5845)
  if (param == null) {
    param = {};
  }
  const {tableItemCid} = param;
  const cid = editorProps.getCid(item);
  const {table} = itemProps;
  const tableProvenanceUuid =
    editorProps.getProvenanceId && editorProps.getProvenanceId(item) != null
      ? generatePlainTableProvenanceUuid(editorProps.getProvenanceId(item)!)
      : undefined;
  return (
    <TableEditable
      fileId={editorProps.fileId}
      // Editable props
      autoCollapseLimit={BenchlingConfig.ELN_PLAIN_TABLE_ROW_LIMIT_FOR_AUTO_COLLAPSE}
      cid={cid}
      collapseId={createCollapseId(this.props.fileId, table.name)}
      indentation={typeof this.getIndentation === 'function' ? this.getIndentation(item) : undefined}
      updateHandler={this.tableUpdateHandler}
      deleteHandler={this.itemDeleteHandler}
      setSelectionAbove={this.setSelectionAbove}
      setSelectionBelow={this.setSelectionBelow}
      mouseDownHandler={this.itemMouseDownHandler}
      mouseOverHandler={this.itemMouseOverHandler}
      insertItemHandler={this.insertItemHandler}
      storeState={editorProps.storeState}
      restoreState={editorProps.restoreState}
      requestSelection={editorProps.requestSelection}
      metadataForRestore={itemProps.metadataForRestore}
      index={index}
      moveHandler={this.moveHandler}
      tabHandler={this.tabHandler}
      setClipboard={this.copyItemWithoutText}
      isTableNameUsed={editorProps.isTableNameUsed}
      canRename={!this.isProtectedAgainstEdit?.()}
      canAddRemoveColumns={!this.isProtectedAgainstEdit?.()}
      // Spreadsheet props
      table={table}
      proxyProvenanceUuid={tableProvenanceUuid}
      tableNamespace={itemProps.tableNamespace}
      showExcessLabels={itemProps.showExcessLabels}
      // Simple Tables alreay show excess row labels, whether showExcessLabels is true or not
      showTableRowExcessLabels={false}
      computedTable={itemProps.computedTable}
      cellColors={itemProps.cellColors}
      viewer={editorProps.viewer}
      showFormulaBox={!editorProps.disableFormatting}
      tryInsert={editorProps.tryInsert}
      canInsert={editorProps.canInsert}
      // Captions
      tableItemCid={tableItemCid}
      // Multi-select props
      setMultiEditingForTable={editorProps.setMultiEditingForTable}
      multiEditing={itemProps.multiEditing}
      isProtected={this.isProtectedAgainstEdit?.()}
      locked={editorProps.locked}
      dirty={editorProps.dirty}
      // Selector props
      selectorsLink={{
        value: itemProps.selectors,
        requestChange: !editorProps.locked ? editorProps.updateSelectors : undefined,
        selectorUsageCounts: itemProps.selectorCounts,
      }}
      openManageSelectorsModal={this.openManageSelectorsModal}
      // Full Screen mode
      disableMaximizedTables={editorProps.disableMaximizedTables}
      isMaximized={itemProps.isMaximized}
      isSplitScreen={itemProps.isSplitScreen}
      isFocused={itemProps.isFocused}
      setMaximizedTable={this.setMaximizedTable}
      setSplitScreenTable={this.setSplitScreenTable}
      isCollapsible={true}
      headerControls={({isCollapsed}) => {
        if (isCollapsed) {
          return null;
        }
        return (
          <>
            <PlainTableSuggestedPlotsButton
              fileId={editorProps.fileId}
              table={table}
              computedTable={itemProps.computedTable}
            />
            <SimpleDownloadAsCSVButton
              onDownloadAsCSV={handleDownloadAsCSV}
              onDownloadParams={{table, computedTable: itemProps.computedTable}}
            />
          </>
        );
      }}
      dataTestAttrs={{component: 'TableEditable', element: 'plainTable'}}
      banner={itemProps.banner}
      {...searchResultProps}
    />
  );
}

const tablePlugin: MediocrePluginComponent<Editor, EditorProps<unknown>, unknown> & {
  title: string;
  default: (this: Editor, rows: number, cols: number, param: Optional<{isWellPlate?: boolean}>) => Table;
} = {
  ...blockPluginBase,
  title: 'Table', // TODO: Remove - unused
  default(this: Editor, rows: number, cols: number, param: Optional<{isWellPlate?: boolean}>): Table {
    if (param == null) {
      param = {};
    }
    const {isWellPlate} = param;
    const usedTableNames = this.props.usedTableNames();
    const tableName = excess.util.uniqueTableName(
      usedTableNames,
      isWellPlate ? TABLE_NAME_WELL_PREFIX : undefined
    );
    return TableUtil.defaultContent(tableName, rows, cols, {isWellPlate});
  },

  required: [
    'getRoot',
    'itemUpdateHandler',
    'itemDeleteHandler',
    'itemMouseDownHandler',
    'itemMouseOverHandler',
    'moveHandler',
    'setSelectionAbove',
    'setSelectionBelow',
    'tabHandler',
  ],

  selectionInit: tableFullSelection,
  selectionAtStart: tableFullSelection,
  selectionAtEnd: tableFullSelection,
  getItemProps: getTableItemProps,
  make(
    this: Editor,
    item: unknown,
    index: number,
    editorProps: EditorProps<unknown>,
    itemProps: TableItemProps,
    searchResultProps: MediocreSearchResultProps
  ) {
    // Note: Do not directly access @props or @state or indirectly through a method call here (D5845)
    return makeTable.call(this, item, index, editorProps, itemProps, searchResultProps);
  },
};

export interface CaptionedItemProps<TChildItemProps> {
  editorWidth: number;
  childItemProps: TChildItemProps;
  isSelected: boolean;
}

interface CaptionBoundProps {
  tabHandler: (index: number, e: TabEvent) => void;
  pasteHandler: (index: number, e: PasteEvent) => void;
  detachHandler: (index: number) => void;
  updateHandler: (
    index: number,
    args: {
      text: string;
      formats: Format;
      selection?: BenchlingSelection;
    }
  ) => void;
  formatHandler: (index: number, args: {format: string; partition: SelectionPartition}) => void;
  moveHandler: (index: number, args: {up: boolean; beginning: boolean}) => void;
  multiSelectHandler: (index: number, args: {up: boolean}) => void;
  splitHandler: (index: number) => void;
  mergeUpHandler: (index: number, args: {anchor: boolean}) => void;
  setClipboard: (index: number, e: ElementClipboardEvent) => void;
}

interface CaptionComponent<TEditor extends Editor<TRoot, TItem>, TRoot, TItem, TChildItemProps> extends Omit<
  MediocrePluginComponent<TEditor, EditorProps<TItem>, TItem>,
  'selectionAtStart' | 'selectionAtEnd' | 'selectionInit' | 'make'
> {
  selectionInit?: MediocrePluginComponent<TEditor, EditorProps<TItem>, TItem>['selectionInit'];
  make: (
    this: TEditor,
    item: TItem,
    index: number,
    editorProps: EditorProps<TItem>,
    itemProps: object,
    searchResultProps: MediocreSearchResultProps,
    resizeProps: {setItemWidth: (itemWidth: number) => void; itemWidth: number}
  ) => JSX.Element;
  makeFooter?: (
    editor: TEditor,
    item: TItem,
    index: number,
    editorProps: EditorProps<TItem>,
    itemProps: TChildItemProps
  ) => JSX.Element | null;
}

function captionWrapper<TEditor extends Editor<TRoot, TItem>, TRoot, TItem, TChildItemProps>(
  plugin: CaptionComponent<TEditor, TRoot, TItem, TChildItemProps>,
  options?: Optional<{tableMode?: boolean}>
): MediocrePluginComponent<TEditor, EditorProps<TItem>, TItem> {
  if (options == null) {
    options = {};
  }
  const tableMode = options.tableMode != null ? options.tableMode : false;

  function captionSelectionAtStart(this: TEditor, item: TItem): MediocreSelection {
    return CaptionEditable.selectionAtStart(
      this.props.getCid(item),
      this.props.getContent(item) as PreviewPluginContent | TablePluginContent
    );
  }

  function captionSelectionAtEnd(this: TEditor, item: TItem): MediocreSelection {
    return CaptionEditable.selectionAtEnd(
      this.props.getCid(item),
      this.props.getContent(item) as PreviewPluginContent | TablePluginContent
    );
  }
  return _.chain({})
    .extend(plugin, {
      selectionAtStart: captionSelectionAtStart,
      selectionAtEnd: captionSelectionAtEnd,
      required: _.union(plugin.required, [
        'textItemUpdateHandler',
        'moveHandler',
        'formatAtIndexHandler',
        'textItemMergeUpHandler',
        'multiArrowSelectHandler',
        'itemDetachHandler',
        'getPluginByItem',
        'tabHandler',
        'captionSplitHandler',
        'copyItemWithoutText',
        'isLockedWithEditWarn',
      ]),

      // This is a higher-order plugin, which wraps an existing plugin. It notably wraps the the itemProps
      // needed by the wrapped plugging in its own getItemProps so it can then thread this through when
      // calling the wrapped plugin's make method in its own make method.
      getItemProps(this: TEditor, item: TItem, index: number): CaptionedItemProps<TChildItemProps> {
        return {
          editorWidth: this.state.editorWidth,
          childItemProps:
            plugin.getItemProps != null ? plugin.getItemProps.call(this, item, index) : undefined,
          isSelected: this.state.selected.index === index && this.state.selected.subIndex === 'item',
        };
      },
      make(
        this: TEditor,
        item: TItem,
        index: number,
        editorProps: EditorProps<TItem>,
        itemProps: CaptionedItemProps<TChildItemProps>,
        searchResultProps: MediocreSearchResultProps
      ) {
        // Note: Do not directly access @props or @state or indirectly through a method call here (D5845)
        const cid = editorProps.getCid(item);
        const content = editorProps.getContent(item);
        const contentProps = {tableMode, content} as CaptionEditableContentProps;
        return (
          <IntermediateBinder<CaptionBoundProps, number>
            key={cid}
            boundArg={index}
            tabHandler={this.tabHandler}
            pasteHandler={this.pasteHandler}
            detachHandler={this.itemDetachHandler}
            updateHandler={this.textItemUpdateHandler}
            formatHandler={this.formatAtIndexHandler}
            moveHandler={this.moveHandler}
            multiSelectHandler={this.multiArrowSelectHandler}
            splitHandler={this.captionSplitHandler}
            mergeUpHandler={this.textItemMergeUpHandler}
            setClipboard={this.copyItemWithoutText}
          >
            {(boundProps) => {
              const footer = plugin.makeFooter
                ? plugin.makeFooter(this, item, index, editorProps, itemProps.childItemProps)
                : null;
              // For cases where we use the footer, we want a gray background behind the entire
              // thing on selection.
              const hasLargeSelection = footer && itemProps.isSelected;
              return (
                <div
                  className={classNames({
                    'mediocre-captionEditable-wrapper--selected': hasLargeSelection,
                  })}
                >
                  {hasLargeSelection && (
                    <span className="mediocre-captionEditable-wrapper-extension--selected" />
                  )}
                  <CaptionEditable
                    cid={cid}
                    indentation={
                      typeof this.getIndentation === 'function' ? this.getIndentation(item) : undefined
                    }
                    index={index}
                    allowMultiline={true}
                    storeState={editorProps.storeState}
                    restoreState={editorProps.restoreState}
                    requestSelection={editorProps.requestSelection}
                    locked={editorProps.locked}
                    spellCheck={editorProps.spellCheck}
                    editorWidth={itemProps.editorWidth}
                    isLockedWithEditWarn={this.isLockedWithEditWarn}
                    isSelected={itemProps.isSelected}
                    isProtected={this.isProtectedAgainstEdit?.()}
                    insertItemHandler={this.insertItemHandler}
                    {...contentProps}
                    {...searchResultProps}
                    {...boundProps}
                  >
                    {(childProps) =>
                      plugin.make.call(
                        this,
                        item,
                        index,
                        editorProps,
                        itemProps.childItemProps,
                        searchResultProps,
                        childProps
                      )
                    }
                  </CaptionEditable>
                  {footer}
                </div>
              );
            }}
          </IntermediateBinder>
        );
      },
    })
    .defaults({selectionInit: captionSelectionAtStart})
    .value() as unknown as MediocrePluginComponent<TEditor, EditorProps<TItem>, TItem>;
}

const plugins = {
  makeTable,
  blockPluginBase,
  selectablePluginBase,
  table: tablePlugin,
  captionWrapper,
  getTableItemProps,
};
export default plugins;
